import { memo } from "react";

import type { TMessage } from "@stores/chat-domain.types";

import { BlockedNotice } from "@conversation-panel/blocked-notice";
import { Sender } from "@conversation-panel/sender";
import { Message } from "@conversation-panel/message";
import { VehicleMessage } from "@conversation-panel/vehicle-message";
import { AntiFraudMessage } from "@conversation-panel/anti-fraud-message";
import { SensitiveDataWarning } from "@conversation-panel/sensitive-data-warning";

import { mergeRefs } from "../../utils/conversation-panel-section.helpers";
import * as S from "../../conversation-panel-section.styles";

import { useConversationArea } from "./conversation-area.hook";
import type {
  TMessageConfig,
  TVehicleMessageConfig,
} from "./conversation-area.types";

const ConversationArea = memo(() => {
  const {
    messages,
    isBlocked,
    isSellingTab,
    isRenderSender,
    scrollerRef,
    scrollDownElement,
    sensitiveDataWarning,
    toggleSensitiveDataWarning,
  } = useConversationArea();

  return (
    <>
      <S.Wrapper>
        {isBlocked ? (
          <S.BlockedArea
            data-testid="blocked-conversation-area"
            data-qa="blocked-conversation-area"
          />
        ) : (
          <>
            <S.Scroller ref={mergeRefs(scrollDownElement, scrollerRef)}>
              <S.MessagesContainer>
                {(messages || []).map((message: TMessage, index: number) => {
                  const detail = message.detail as
                    | TVehicleMessageConfig
                    | undefined;
                  return (
                    <Message
                      key={`${message.sentOn}-${message.senderId}-${index}`}
                      data={message as TMessageConfig["data"]}
                      itsMyMessage={!!message.itsMyMessage}
                      status={message.statusMessage as TMessageConfig["status"]}
                    >
                      {detail?.advertiseId && <VehicleMessage {...detail} />}
                    </Message>
                  );
                })}
                <AntiFraudMessage />
              </S.MessagesContainer>
            </S.Scroller>
            <SensitiveDataWarning
              sensitiveDataWarning={sensitiveDataWarning}
              toggleSensitiveDataWarning={toggleSensitiveDataWarning}
            />
          </>
        )}
      </S.Wrapper>
      {isBlocked && (
        <footer>
          <BlockedNotice isSellingTab={isSellingTab} />
        </footer>
      )}
      {!isBlocked && isRenderSender && (
        <footer>
          <Sender
            sensitiveDataWarning={sensitiveDataWarning}
            toggleSensitiveDataWarning={toggleSensitiveDataWarning}
          />
        </footer>
      )}
    </>
  );
});

ConversationArea.displayName = "ConversationArea";
export { ConversationArea };
